<template>
  <div class="autodesk-corp-page">
    <div class="autodesk-corp-card">
      <h2 class="autodesk-corp-title">
        <i class="fa fa-cloud"></i>
        {{ $t('autodesk.corporate.title') }}
      </h2>
      <p class="autodesk-corp-hint">{{ $t('autodesk.corporate.hint') }}</p>

      <div v-if="loading" class="autodesk-corp-loading">
        <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
      </div>

      <div v-else-if="connected" class="autodesk-corp-connected">
        <div class="autodesk-corp-status">
          <b-icon v-if="needsReauth" pack="fas" icon="exclamation-triangle" custom-class="has-text-warning" />
          <b-icon v-else pack="fas" icon="check-circle" custom-class="has-text-success" />
          <div>
            <div>{{ $t('autodesk.status.connected_as') }} <strong>{{ email }}</strong></div>
            <div class="autodesk-corp-meta" v-if="connectedBy">
              {{ $t('autodesk.corporate.connected_by', { name: connectedBy }) }}
            </div>
            <div v-if="needsReauth" class="has-text-warning-dark mt-1">
              {{ $t('autodesk.corporate.needs_reauth') }}
            </div>
          </div>
        </div>
        <div class="buttons">
          <!-- Token roto (invalid_grant): reconectar sobreescribe el token sin perder la ACL de proyectos. -->
          <button v-if="needsReauth" class="button is-primary" @click="connect" :disabled="busy">
            <b-icon pack="fas" icon="plug" size="is-small" />
            <span>{{ $t('autodesk.button.reconnect') }}</span>
          </button>
          <button class="button is-danger is-light" @click="disconnect" :disabled="busy">
            <b-icon pack="fas" icon="sign-out-alt" size="is-small" />
            <span>{{ $t('autodesk.button.disconnect') }}</span>
          </button>
        </div>
      </div>

      <div v-else class="autodesk-corp-disconnected">
        <p>{{ $t('autodesk.corporate.not_connected_hint') }}</p>
        <button class="button is-primary" @click="connect" :disabled="busy">
          <b-icon pack="fas" icon="plug" size="is-small" />
          <span>{{ $t('autodesk.button.connect') }}</span>
        </button>
      </div>

      <div v-if="error" class="autodesk-corp-error">
        <b-icon pack="fas" icon="exclamation-triangle" />
        <span class="ml-2">{{ error }}</span>
      </div>
    </div>

    <!-- Tabla de proyectos EPC + su ACL Autodesk. Solo cuando hay conexión. -->
    <div v-if="connected" class="autodesk-corp-card">
      <h3 class="autodesk-corp-subtitle">
        <b-icon pack="fas" icon="tasks" size="is-small" />
        {{ $t('autodesk.corporate.projects_title') }}
      </h3>
      <p class="autodesk-corp-hint">{{ $t('autodesk.corporate.projects_hint') }}</p>

      <div v-if="loadingProjects" class="autodesk-corp-loading">
        <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
      </div>

      <div v-else-if="projects.length === 0" class="autodesk-corp-empty">
        <p>{{ $t('autodesk.corporate.no_projects') }}</p>
      </div>

      <table v-else class="autodesk-corp-table">
        <thead>
          <tr>
            <th>{{ $t('project') }}</th>
            <th class="has-text-centered">{{ $t('autodesk.corporate.col_resources') }}</th>
            <th class="has-text-right">{{ $t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in projects" :key="p.id">
            <td>
              <div class="autodesk-corp-project-name">{{ p.name }}</div>
              <div v-if="p.reference" class="autodesk-corp-project-code">{{ p.reference }}</div>
            </td>
            <td class="has-text-centered">
              <span class="autodesk-corp-count-badge"
                    :class="{ 'is-empty': p.autodesk_resource_count === 0 }">
                {{ p.autodesk_resource_count }}
              </span>
            </td>
            <td class="has-text-right">
              <button class="button is-small is-primary" @click="openConfig(p)">
                <b-icon pack="fas" icon="cog" size="is-small" />
                <span>{{ $t('autodesk.resources.manage') }}</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Drawer lateral derecho con la gestión de recursos del proyecto -->
    <autodesk-project-resources
      :visible="configModalOpen"
      :project-id="configProjectId || 0"
      :project-name="configProjectName"
      @close="onConfigClose"
    />
  </div>
</template>

<script>
import axios from 'axios'
import store from '~/store'
import autodeskProjectResources from '·/components/autodesk/autodesk-project-resources.vue'

const RETURN_TO_KEY = 'autodesk_oauth_return_to'

export default {
  name: 'admin-autodesk',
  middleware: 'auth',
  title: 'autodesk',
  subtitle: 'autodesk.corporate.page_subtitle',
  menuOption: '0-11',

  components: { autodeskProjectResources },

  data: () => ({
    loading: true,
    busy: false,
    connected: false,
    email: null,
    connectedBy: null,
    needsReauth: false,
    error: null,

    loadingProjects: false,
    projects: [],

    configModalOpen: false,
    configProjectId: null,
    configProjectName: '',
  }),

  async mounted() {
    await this.loadStatus()
    if (this.connected) await this.loadProjects()
  },

  methods: {
    async loadStatus() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/v2/autodesk/aps/status', this.authHeader())
        if (data.success && data.data.connected) {
          this.connected = true
          this.email = data.data.email
          this.connectedBy = data.data.connected_by
          this.needsReauth = !!data.data.needs_reauth
        }
      } catch (e) {
        this.error = this.errorMessage(e)
      } finally {
        this.loading = false
      }
    },

    async loadProjects() {
      this.loadingProjects = true
      try {
        const { data } = await axios.get('/api/v2/autodesk/aps/company/projects', this.authHeader())
        if (data.success) this.projects = data.data
      } catch (e) {
        this.error = this.errorMessage(e)
      } finally {
        this.loadingProjects = false
      }
    },

    async connect() {
      this.busy = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v2/autodesk/aps/oauth/start', this.authHeader())
        if (data.success && data.data.url) {
          sessionStorage.setItem(RETURN_TO_KEY, this.$route.fullPath)
          window.location.href = data.data.url
        } else {
          this.error = data.message || this.$t('autodesk.error.oauth_start_failed')
        }
      } catch (e) {
        this.error = this.errorMessage(e)
      } finally {
        this.busy = false
      }
    },

    disconnect() {
      this.$buefy.dialog.confirm({
        title: this.$t('autodesk.button.disconnect'),
        message: this.$t('autodesk.corporate.confirm_disconnect'),
        confirmText: this.$t('autodesk.button.disconnect'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.busy = true
          try {
            await axios.post('/api/v2/autodesk/aps/oauth/disconnect', {}, this.authHeader())
            this.connected = false
            this.email = null
            this.connectedBy = null
            this.needsReauth = false
            this.projects = []
          } catch (e) {
            this.error = this.errorMessage(e)
          } finally {
            this.busy = false
          }
        },
      })
    },

    openConfig(project) {
      this.configProjectId = project.id
      this.configProjectName = project.name || ''
      this.configModalOpen = true
    },

    // Al cerrar el drawer recargamos el count por si añadió/quitó recursos.
    async onConfigClose() {
      this.configModalOpen = false
      await this.loadProjects()
      this.configProjectId = null
      this.configProjectName = ''
    },

    authHeader() {
      return { headers: { Authorization: 'Bearer ' + store.getters['auth/token'] } }
    },

    // El interceptor de axios rechaza con el response (no con un Error), y con
    // undefined si falla la red; un error lanzado en el propio try sí es un Error.
    errorMessage(e) {
      return e?.data?.message || e?.message || this.$t('error_alert_text')
    },
  },
}
</script>

<style scoped>
.autodesk-corp-page {
  padding: 24px;
}

.autodesk-corp-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 24px;
  max-width: 960px;
  margin-bottom: 16px;
}

.autodesk-corp-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2f374a;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.autodesk-corp-subtitle {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2f374a;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.autodesk-corp-hint {
  color: #5d6b82;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.autodesk-corp-loading,
.autodesk-corp-empty {
  padding: 24px;
  text-align: center;
  color: #5d6b82;
}

.autodesk-corp-connected,
.autodesk-corp-disconnected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f5f7fb;
  border-radius: 6px;
  gap: 16px;
}

.autodesk-corp-disconnected {
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.autodesk-corp-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.autodesk-corp-meta {
  font-size: 0.8rem;
  color: #888;
  margin-top: 2px;
}

.autodesk-corp-error {
  margin-top: 16px;
  padding: 12px;
  background: #fff5f5;
  color: #c53030;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.autodesk-corp-table {
  width: 100%;
  border-collapse: collapse;
}

.autodesk-corp-table th,
.autodesk-corp-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.autodesk-corp-table th {
  font-size: 0.8rem;
  color: #5d6b82;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
  background: #f5f7fb;
}

.autodesk-corp-project-name {
  font-weight: 500;
  color: #2f374a;
}

.autodesk-corp-project-code {
  font-size: 0.8rem;
  color: #888;
  margin-top: 2px;
}

.autodesk-corp-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 2px 10px;
  background: #3273dc;
  color: #fff;
  font-weight: 600;
  border-radius: 12px;
  font-size: 0.85rem;
}

.autodesk-corp-count-badge.is-empty {
  background: #dde2ea;
  color: #5d6b82;
}
</style>
